import { useState, useEffect } from "react";
import { X, Apple, Smartphone } from "lucide-react";
import { Button } from "@/components/ui/button";
import { usePlatform } from "@/hooks/usePlatform";
import { APP_STORE_URL, isIOSWebBrowser, trackCtaClick } from "@/data/appLinks";

interface AppInstallBannerProps {
  airportCode: string;
  /** Called with true when this banner takes the slot, so other banners can stand down. */
  onVisibilityChange?: (visible: boolean) => void;
}

const VISITS_KEY = "awb_app_visit_counts";
const DISMISSED_KEY = "awb_app_install_dismissed";

const incrementVisit = (code: string): number => {
  try {
    const counts = JSON.parse(localStorage.getItem(VISITS_KEY) || "{}");
    counts[code] = (counts[code] || 0) + 1;
    localStorage.setItem(VISITS_KEY, JSON.stringify(counts));
    return Object.values(counts).reduce<number>((a, b: any) => a + Number(b), 0);
  } catch {
    return 0;
  }
};

const isDismissed = (): boolean => {
  try {
    const ts = localStorage.getItem(DISMISSED_KEY);
    if (!ts) return false;
    return Date.now() - Number(ts) < 7 * 24 * 60 * 60 * 1000;
  } catch {
    return false;
  }
};

export const AppInstallBanner = ({ airportCode, onVisibilityChange }: AppInstallBannerProps) => {
  const { isNative, isPWA } = usePlatform();
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    if (isNative || isPWA || isDismissed()) return;
    const totalVisits = incrementVisit(airportCode);
    if (totalVisits < 2) return;
    const timer = setTimeout(() => setVisible(true), 2000);
    return () => clearTimeout(timer);
  }, [airportCode, isNative, isPWA]);

  useEffect(() => {
    onVisibilityChange?.(visible);
  }, [visible, onVisibilityChange]);

  if (!visible) return null;

  const dismiss = () => {
    setVisible(false);
    localStorage.setItem(DISMISSED_KEY, String(Date.now()));
  };

  const onIOS = isIOSWebBrowser();

  return (
    <div className="fixed bottom-4 left-4 right-4 md:left-auto md:right-6 md:max-w-md z-50 animate-slide-in-bottom">
      <div className="bg-card border border-border shadow-md rounded-xl p-4 flex items-start gap-3">
        <span className="text-primary mt-0.5 flex-shrink-0">
          {onIOS ? <Apple className="h-5 w-5" /> : <Smartphone className="h-5 w-5" />}
        </span>
        <div className="flex-1 min-w-0">
          <p className="text-base text-foreground">
            Track {airportCode} — we'll push you the moment the line crosses 20 minutes, and remind you
            when to leave. Free.
          </p>
          <Button
            asChild
            size="sm"
            className="mt-2 min-h-[44px] rounded-xl active:scale-95"
            onClick={() => trackCtaClick("airport_install_banner", airportCode)}
          >
            <a href={APP_STORE_URL} target="_blank" rel="noopener noreferrer">
              Get the free app
            </a>
          </Button>
        </div>
        <button
          onClick={dismiss}
          className="text-muted-foreground hover:text-foreground p-1 min-h-[44px] min-w-[44px] flex items-center justify-center"
          aria-label="Dismiss"
        >
          <X className="h-4 w-4" />
        </button>
      </div>
    </div>
  );
};
